import { Alert, Button, Card, useToast } from '@catering/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Save } from 'lucide-react';
import { type JSX, useState } from 'react';

import {
  BILLING_CYCLE_LABELS,
  SERVICE_DAY_LABELS,
  activateContract,
  pauseContract,
  terminateContract,
  updateContract,
  type ContractDetail,
  type ContractUpdateInput,
} from '../../../contracts/api';
import {
  diffChanges,
  mapContractError,
  type ContractFormValues,
} from '../../../contracts/contract-form-schema';
import { hasPermission } from '../../../auth/has-permission';
import { mapTransitionError } from '../../../contracts/contract-api-errors';
import type { CustomerListItem } from '../../../customers/api';
import type { CurrentUser } from '../../../auth/use-current-user';
import { useOperatingUnitOptions } from '../../../operating-units/use-operating-unit-options';
import { ContractForm } from '../contract-form-page';
import { ContractTransitionButtons } from './contract-transition-buttons';

export interface ContractConditionsTabProps {
  contract: ContractDetail;
  customers: CustomerListItem[];
  currentUser: CurrentUser | undefined;
  onRefetch: () => void;
}

/**
 * `contracts/spec.md` → "Botón de activación deshabilitado sin permiso",
 * "Edición de contrato activo genera nueva versión". Reuses `ContractForm`
 * for edit mode, same criterion `CustomerDataTab` already applies to
 * `CustomerForm`.
 */
export function ContractConditionsTab({
  contract,
  customers,
  currentUser,
  onRefetch,
}: ContractConditionsTabProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const unitOptions = useOperatingUnitOptions();
  const unitName = unitOptions.find((unit) => unit.id === contract.operatingUnitId)?.name;

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['contract', contract.id] });
    await queryClient.invalidateQueries({ queryKey: ['contracts'] });
    onRefetch();
  }

  const updateMutation = useMutation({
    mutationFn: (input: ContractUpdateInput) => updateContract(contract.id, input),
    onSuccess: async () => {
      await invalidate();
      setMode('view');
      toast('success', 'Contrato actualizado');
    },
  });

  const activateMutation = useMutation({
    mutationFn: () => activateContract(contract.id),
    onSuccess: () => {
      void invalidate();
      toast('success', 'Contrato activado');
    },
  });

  const pauseMutation = useMutation({
    mutationFn: (reason: string | null) => pauseContract(contract.id, reason),
    onSuccess: () => {
      void invalidate();
      toast('success', 'Contrato pausado');
    },
  });

  const terminateMutation = useMutation({
    mutationFn: (reason: string) => terminateContract(contract.id, reason),
    onSuccess: () => {
      void invalidate();
      toast('success', 'Contrato dado de baja');
    },
  });

  if (mode === 'edit') {
    const { formError, fieldError } = mapContractError(updateMutation.error);

    return (
      <Card>
        <ContractForm
          mode="edit"
          contractStatus={contract.status}
          customers={customers}
          submitLabel="Guardar cambios"
          submitIcon={Save}
          submitting={updateMutation.isPending}
          {...(formError === undefined ? {} : { formError })}
          {...(fieldError === undefined ? {} : { fieldError })}
          onCancel={() => setMode('view')}
          defaultValues={toFormValues(contract)}
          onSubmit={async (values) => {
            const changes = diffChanges(contract, values);
            const withEffectiveDate: ContractUpdateInput =
              contract.status === 'ACTIVE'
                ? { ...changes, effectiveDate: new Date().toISOString() }
                : changes;

            await updateMutation.mutateAsync(withEffectiveDate).catch(() => undefined);
          }}
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {activateMutation.isError ? (
        <Alert tone="danger">{mapTransitionError(activateMutation.error, 'activar')}</Alert>
      ) : null}
      {pauseMutation.isError ? (
        <Alert tone="danger">{mapTransitionError(pauseMutation.error, 'pausar')}</Alert>
      ) : null}
      {terminateMutation.isError ? (
        <Alert tone="danger">{mapTransitionError(terminateMutation.error, 'dar de baja')}</Alert>
      ) : null}

      <Card>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-slate-900">Condiciones comerciales</h2>
          {hasPermission(currentUser, 'contract.update') ? (
            <Button variant="secondary" size="sm" icon={Pencil} onClick={() => setMode('edit')}>
              Editar
            </Button>
          ) : null}
        </div>

        <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
          <Item label="Punto de venta" value={unitName ?? '—'} />
          <Item label="Ciclo de facturación" value={BILLING_CYCLE_LABELS[contract.billingCycle]} />
          <Item label="Plazo de pago" value={`${contract.paymentTermDays} días`} />
          <Item label="Hora de corte efectiva" value={contract.effectiveCutoffTime} />
          <Item
            label="Días de anticipación de corte"
            value={String(contract.effectiveCutoffDaysBefore)}
          />
          <Item label="Tope mínimo diario" value={contract.minDailyQuantity?.toString() ?? '—'} />
          <Item label="Tope máximo diario" value={contract.maxDailyQuantity?.toString() ?? '—'} />
          <Item label="Días de servicio" value={formatServiceDays(contract.serviceDays)} />
          <Item label="Incluye feriados" value={contract.includesHolidays ? 'Sí' : 'No'} />
          <Item label="Notas" value={contract.notes ?? '—'} />
        </dl>

        <div className="mt-6 border-t border-slate-100 pt-5">
          <ContractTransitionButtons
            contract={contract}
            currentUser={currentUser}
            activating={activateMutation.isPending}
            pausing={pauseMutation.isPending}
            terminating={terminateMutation.isPending}
            onActivate={() => activateMutation.mutate()}
            onPause={(reason) => pauseMutation.mutate(reason)}
            onTerminate={(reason) => terminateMutation.mutate(reason)}
          />
        </div>
      </Card>
    </div>
  );
}

function Item({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-slate-900">{value}</dd>
    </div>
  );
}

function formatServiceDays(days: number[]): string {
  if (days.length === 0) {
    return '—';
  }

  return [...days]
    .sort((a, b) => a - b)
    .map((day) => SERVICE_DAY_LABELS[day as keyof typeof SERVICE_DAY_LABELS])
    .join(', ');
}

function toFormValues(contract: ContractDetail): ContractFormValues {
  return {
    code: contract.code,
    customerId: contract.customerId,
    operatingUnitId: contract.operatingUnitId,
    startDate: contract.startDate.slice(0, 10),
    endDate: contract.endDate === null ? '' : contract.endDate.slice(0, 10),
    billingCycle: contract.billingCycle,
    paymentTermDays: String(contract.paymentTermDays),
    cutoffTime: contract.cutoffTime ?? '',
    cutoffDaysBefore: contract.cutoffDaysBefore === null ? '' : String(contract.cutoffDaysBefore),
    minDailyQuantity: contract.minDailyQuantity === null ? '' : String(contract.minDailyQuantity),
    maxDailyQuantity: contract.maxDailyQuantity === null ? '' : String(contract.maxDailyQuantity),
    allowsCutoffOverride: contract.allowsCutoffOverride,
    serviceDays: contract.serviceDays,
    includesHolidays: contract.includesHolidays,
    notes: contract.notes ?? '',
  };
}
